import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

export const resolveMediaUrl = (value) => {
  if (!value || typeof value !== 'string') return value;
  if (/^https?:\/\//i.test(value)) return value;

  const mediaBase = (process.env.REACT_APP_OPENINARY_URL || 'https://images.ferramentum.se').replace(/\/$/, '');
  const path = value.startsWith('/t/') ? value : `/t/${value.replace(/^\/+/, '')}`;
  return `${mediaBase}${path}`;
};

API.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403) {
      sessionStorage.removeItem('token');
      window.location.href = '/';
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return API(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const { data } = await axios.post(
        `${apiBase}/api/auth/refresh`,
        {},
        { timeout: 15000 }
      );

      const newAccess = data.accessToken;
      sessionStorage.setItem('token', newAccess);

      processQueue(null, newAccess);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return API(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      sessionStorage.removeItem('token');

      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const login = (data) => API.post('/api/auth/admin/login', data);
export const logout = () => API.post('/api/auth/logout');

export const getDashboard = () => API.get('/api/admin/dashboard');

export const getProducts = () => API.get('/api/admin/products');
export const createProduct = (data) => API.post('/api/admin/products', data);
export const updateProduct = (id, data) => API.put(`/api/admin/products/${id}`, data);
export const deleteProduct = (id) => API.delete(`/api/admin/products/${id}`);

export const getOrders = (status) => {
  const params = status ? { status } : {};
  return API.get('/api/admin/orders', { params });
};
export const getOrder = (id) => API.get(`/api/admin/orders/${id}`);
export const updateOrder = (id, data) => API.put(`/api/admin/orders/${id}`, data);
export const getOrderMapUrl = (id) => API.get(`/api/admin/orders/${id}/map`);

export const getUsers = () => API.get('/api/admin/users');
export const getUser = (id) => API.get(`/api/admin/users/${id}`);
export const suspendUser = (id, data) => API.post(`/api/admin/users/${id}/suspend`, data);
export const unsuspendUser = (id) => API.post(`/api/admin/users/${id}/unsuspend`);
export const deleteUser = (id) => API.delete(`/api/admin/users/${id}`);
export const editUser = (id, data) => API.put(`/api/admin/users/${id}`, data);

export const getDiscounts = () => API.get('/api/admin/discounts/');
export const createDiscount = (data) => API.post('/api/admin/discounts/', data);
export const updateDiscount = (id, data) => API.put(`/api/admin/discounts/${id}`, data);
export const toggleDiscount = (id) => API.patch(`/api/admin/discounts/${id}/toggle`);
export const deleteDiscount = (id) => API.delete(`/api/admin/discounts/${id}`);

export const getCategories = () => API.get('/api/admin/categories');
export const createCategory = (data) => API.post('/api/admin/categories', data);
export const updateCategory = (id, data) => API.put(`/api/admin/categories/${id}`, data);
export const deleteCategory = (id) => API.delete(`/api/admin/categories/${id}`);

export const getTickets = () => API.get('/api/admin/tickets');
export const getTicketById = (id) => API.get(`/api/admin/tickets/${id}`);
export const filterStatus = (status) => API.get(`/api/admin/tickets/filter/status/${status}`);
export const replyToTicket = (id, data) => API.post(`/api/admin/tickets/${id}/reply`, data);
export const updateTicketStatus = (id, data) => API.patch(`/api/admin/tickets/${id}/status`, data);
export const updateTicketPriority = (id, data) => API.patch(`/api/admin/tickets/${id}/priority`, data);
export const closeTicket = (id) => API.patch(`/api/admin/tickets/${id}/close`);
export const deleteTicket = (id) => API.delete(`/api/admin/tickets/${id}`);

export const getCheckouts = (status) => {
  const params = status ? { status } : {};
  return API.get('/api/admin/checkouts', { params });
};
export const getCheckout = (id) => API.get(`/api/admin/checkouts/${id}`);
export const sendPasswordResetEmail = (data) => API.post('/api/auth/forgot-password', data);

export const getUploads = () => API.get('/api/admin/uploads');
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/api/admin/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteUpload = (id) => API.delete(`/api/admin/uploads/${id}`);
export const updateOrderStatus = (id, status) =>
  API.put(`/api/admin/orders/${id}/update/${status}`);
